import _ from 'lodash';
import { createSelector } from 'reselect';

import { REDUCER_KEY } from './reducer';
import { getEnvironmentSettings } from 'services/environment';
import { can } from 'services/can';
import { PermissionsMapping } from 'common/constants/role-intents';
import { AUDIENCE_CATEGORY_HIERARCHY } from 'states/resources/audiences/business-logic';

// Transform AUDIENCE_CATEGORY_HIERARCHY to match expected structure for select checkbox filters
const transformHierarchyForAudienceFilters = hierarchy => {
    return hierarchy
        .filter(item => item.value !== 'custom_audiences') // Omit custom_audiences top level section element
        .map(item => ({
            ...item,
            ...(item.sections && { subGroups: item.sections }), // Rename sections to subGroups
            sections: undefined, // Remove original sections field
        }));
};

const filtersByTypeRestructured = transformHierarchyForAudienceFilters(AUDIENCE_CATEGORY_HIERARCHY);

const getFiltersByType = () => {
    return filtersByTypeRestructured;
};

const selectAudiences = state => _.get(state, `${REDUCER_KEY}.audiences`);
const selectFilters = state => _.get(state, `${REDUCER_KEY}.filters`);
export const selectRestrictedSegments = state => _.get(state, 'profile.restrictedSegments', []);
export const selectOrgId = state => _.get(state, 'profile.organizationId', void 0);

export const filterAudiences = () =>
    createSelector(
        [selectAudiences, selectFilters, selectRestrictedSegments, selectOrgId],
        (audiences, filters, restrictedSegments) => {
            const filtersByType = getFiltersByType();

            // Get the source types for selected filters
            let selectedSources;
            // Handle 3-level hierarchy: extract categories from subGroups
            selectedSources = _(filtersByType)
                .map(mainGroup => {
                    if (mainGroup && mainGroup.subGroups) {
                        return _(mainGroup.subGroups)
                            .map(subGroup =>
                                subGroup && subGroup.categories ? subGroup.categories : []
                            )
                            .flatten()
                            .value();
                    } else {
                        return mainGroup && mainGroup.categories ? mainGroup.categories : [];
                    }
                })
                .flatten()
                .filter(
                    category => category && category.value && _.includes(filters, category.value)
                )
                .filter(category => {
                    if (!category || !category.source) {
                        return false;
                    }
                    // Handle both array and string sources for restricted segments check
                    const sources = _.isArray(category.source)
                        ? category.source
                        : [category.source];
                    return !_.some(sources, source => _.includes(restrictedSegments, source));
                })
                .map(category => (_.isArray(category.source) ? category.source : [category.source]))
                .flatten()
                .value();

            if (selectedSources.length > 0) {
                const filteredAudiences = _.filter(audiences, audience => {
                    const match = _.includes(selectedSources, audience.source_type);
                    return match;
                });
                return filteredAudiences;
            }

            return audiences;
        }
    );

export const filteredfiltersByType = () =>
    createSelector(
        [selectRestrictedSegments, selectOrgId],
        restrictedSegments => {
            const filtersByType = getFiltersByType();
            let audienceFiltersByType = _.cloneDeep(filtersByType);
            const excludedAudiences = getEnvironmentSettings().excludedAudiences();

            // Handle 3-level hierarchy: Main Groups -> Sub Groups -> Categories
            _.forEach(filtersByType, (mainGroup, mainIndex) => {
                if (mainGroup && mainGroup.subGroups) {
                    const filteredSubGroups = [];
                    _.forEach(mainGroup.subGroups, subGroup => {
                        if (subGroup && subGroup.categories) {
                            const categories = _(subGroup.categories)
                                .filter(category => category && category.source)
                                .filter(category => {
                                    if (!category || !category.source) {
                                        return false;
                                    }
                                    // Handle both array and string sources
                                    const sources = _.isArray(category.source)
                                        ? category.source
                                        : [category.source];
                                    const isRestrictedSegment = _.some(sources, source =>
                                        _.includes(restrictedSegments, source)
                                    );
                                    const excludedAudienceList =
                                        excludedAudiences[mainGroup.value] || [];
                                    const isAudienceExcluded = _.some(sources, source =>
                                        _.includes(excludedAudienceList, source)
                                    );

                                    if (isRestrictedSegment || isAudienceExcluded) {
                                        return false;
                                    }
                                    return true;
                                })
                                .value();

                            if (categories.length > 0) {
                                filteredSubGroups.push({
                                    ...subGroup,
                                    categories,
                                });
                            }
                        }
                    });

                    audienceFiltersByType[mainIndex].subGroups = filteredSubGroups;
                } else if (mainGroup && mainGroup.categories) {
                    // Fallback for any groups that don't have subGroups
                    const categories = _(mainGroup.categories)
                        .filter(category => category && category.source)
                        .filter(category => {
                            if (!category || !category.source) {
                                return false;
                            }
                            const sources = _.isArray(category.source)
                                ? category.source
                                : [category.source];
                            const isRestrictedSegment = _.some(sources, source =>
                                _.includes(restrictedSegments, source)
                            );
                            const excludedAudienceList = excludedAudiences[mainGroup.value] || [];
                            const isAudienceExcluded = _.some(sources, source =>
                                _.includes(excludedAudienceList, source)
                            );

                            if (isRestrictedSegment || isAudienceExcluded) {
                                return false;
                            }
                            return true;
                        })
                        .value();

                    audienceFiltersByType[mainIndex].categories = categories;
                }
            });

            return audienceFiltersByType;
        }
    );

export const editAudiences = createSelector(() => {
    return can(PermissionsMapping.AUDIENCE__EDIT);
});
